import { useEffect } from "react";
import {
  Zap, ArrowRight, Phone, TrendingUp, Users, Clock, DollarSign,
  Target, AlertTriangle, CheckCircle2, XCircle, Rocket, Calculator,
  Sparkles, Brain, BarChart3,
} from "lucide-react";

const ACUITY = "https://clubviajemos.as.me/?appointmentType=91641310";

const Module = ({ n, kicker, title, children }) => (
  <section className="px-4 py-10 sm:py-14" data-testid={`tol-modulo-${n}`}>
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-5">
        <span className="text-5xl sm:text-6xl font-black text-amber-400/20 leading-none">{String(n).padStart(2, "0")}</span>
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[3px] text-amber-400 mb-1">{kicker}</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white leading-tight">{title}</h2>
        </div>
      </div>
      <div className="space-y-4 text-slate-300 text-base sm:text-lg leading-relaxed">{children}</div>
    </div>
  </section>
);

const Quote = ({ children }) => (
  <p className="border-l-4 border-amber-400 pl-5 italic text-amber-200 text-lg sm:text-xl my-6">{children}</p>
);

const Stat = ({ value, label }) => (
  <div className="text-center">
    <p className="text-3xl sm:text-5xl font-black text-amber-300 leading-none">{value}</p>
    <p className="text-[10px] sm:text-xs text-slate-400 uppercase tracking-wider mt-2">{label}</p>
  </div>
);

const ROLES = [
  "Trafficker digital — campañas en Meta y Google",
  "Diseñador gráfico — creatividades y banners",
  "Especialista en Landing Pages que convierten",
  "Ads Manager — segmentación y pruebas A/B",
  "Community Manager — mensajes y comentarios",
  "Copywriter — textos persuasivos",
  "Video Maker — contenido audiovisual",
  "Experto en Automatizaciones y CRM (GoHighLevel)",
];

const COSTOS = [
  { c: "Equipo de 4 personas (salarios + beneficios)", v: "$450,000 – $550,000" },
  { c: "Herramientas y software (CRM, diseño, automatización)", v: "$50,000+" },
  { c: "Gastos generales por empleado (oficina, equipo)", v: "~$15,000 c/u" },
  { c: "Reclutamiento y entrenamiento por contratación", v: "20–30% del salario" },
  { c: "Tiempo promedio para llenar UN puesto", v: "~50 días" },
];

const ETAPAS = [
  {
    n: "1", t: "0 a 1 mes", name: "El Agente que Recién Empieza",
    box: "border-sky-400/30", num: "text-sky-400", kick: "text-sky-300", meta: "text-sky-300",
    obj: "Aprender el sistema y generar tus primeras conversaciones.",
    leads: "20 a 30 leads/mes ($10–$20/día)",
    conv: "2% a 3%",
    metaTxt: "1 venta en el primer mes",
  },
  {
    n: "2", t: "3 meses", name: "El Agente en Crecimiento",
    box: "border-amber-400/30", num: "text-amber-400", kick: "text-amber-300", meta: "text-amber-300",
    obj: "Consolidar tu proceso y lograr ventas consistentes.",
    leads: "80 a 120 leads/mes ($30–$50/día)",
    conv: "4% a 5%",
    metaTxt: "4 a 6 ventas al mes",
  },
  {
    n: "3", t: "6 meses", name: "El Agente en Productividad Plena",
    box: "border-emerald-400/30", num: "text-emerald-400", kick: "text-emerald-300", meta: "text-emerald-300",
    obj: "Escalar y maximizar el retorno de cada campaña.",
    leads: "300+ leads/mes ($100–$200/día)",
    conv: "5% a 7%",
    metaTxt: "15 a 30 ventas al mes",
  },
];

const COMPARA = [
  { c: "Costo del equipo", solo: "$450,000+/año", te: "$0 — incluido" },
  { c: "Plataformas y software", solo: "$50,000+/año", te: "$0 — incluido" },
  { c: "Tiempo para arrancar", solo: "6 – 8 meses", te: "Inmediato" },
  { c: "Riesgo de rotación", solo: "Alto y constante", te: "Ninguno" },
  { c: "Lo que realmente pagas", solo: "Todo el overhead", te: "Solo el lead real" },
];

const INVERSION = [
  { d: "$10 / día", q: "$150", p: "Arranque inteligente" },
  { d: "$20 / día", q: "$300", p: "Flujo constante" },
  { d: "$30 / día", q: "$450", p: "Mayor volumen" },
  { d: "$50 / día", q: "$750", p: "Escalada rápida" },
  { d: "$100 / día", q: "$1,500", p: "Alto rendimiento" },
  { d: "$200 / día", q: "$3,000", p: "Nivel Empire" },
];

const TonsOfLeadsPage = () => {
  useEffect(() => {
    document.title = "Tons of Leads · Curso Travel Empire · ClubViajemos";
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0a14] via-[#0d0d1c] to-[#0a0a14] text-white" style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }} data-testid="tons-of-leads-page">
      {/* TOP BANNER */}
      <div className="bg-gradient-to-r from-amber-400 via-amber-500 to-amber-400 text-slate-900 text-center py-2 text-xs sm:text-sm font-black tracking-wider sticky top-0 z-40">
        <Sparkles className="inline h-3.5 w-3.5 mr-1.5 mb-0.5" />
        CURSO TRAVEL EMPIRE · TONS OF LEADS · CLUB VIAJEMOS
      </div>

      {/* HERO */}
      <section className="relative px-4 pt-16 pb-12 sm:pt-24 sm:pb-16 overflow-hidden">
        <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-amber-400/10 rounded-full blur-[180px] pointer-events-none" />
        <div className="relative max-w-5xl mx-auto text-center">
          <p className="inline-block bg-amber-400/15 text-amber-300 text-[11px] uppercase tracking-[3px] px-4 py-1.5 rounded-full font-mono mb-6 border border-amber-400/30">
            El sistema de prospección que solo los grandes conocían
          </p>
          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tight mb-5">
            Curso de Negocios<br />
            <span className="text-amber-300">Travel Empire</span>
          </h1>
          <p className="text-base sm:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed mb-3">
            Cómo acceder a la maquinaria de marketing de las grandes corporaciones — a costo cero de equipo — y escalar tu negocio de forma económica, rápida y simple.
          </p>
          <p className="text-sm sm:text-base text-amber-200 max-w-2xl mx-auto mb-10 font-bold">
            Para el agente ClubViajemos · Plan <span className="bg-amber-400/15 px-2 rounded">Tons of Leads</span>
          </p>

          <div className="grid grid-cols-3 gap-4 sm:gap-8 max-w-3xl mx-auto py-7 border-y border-white/10">
            <Stat value="$0" label="Costo de equipo propio" />
            <Stat value="5%" label="Conversión promedio lead→venta" />
            <Stat value="∞" label="Leads sin límite" />
          </div>

          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <a href={ACUITY} target="_blank" rel="noopener noreferrer" data-testid="tol-cta-hero"
              className="inline-flex items-center justify-center gap-2.5 bg-gradient-to-r from-amber-400 to-amber-300 hover:from-amber-300 hover:to-amber-400 text-slate-900 font-black px-7 py-4 rounded-xl uppercase tracking-wider text-sm hover:scale-[1.03] transition-transform shadow-[0_15px_50px_rgba(212,175,55,0.4)]">
              <Phone className="h-5 w-5" /> Activar Travel Empire
            </a>
            <a href="#curso" className="inline-flex items-center justify-center gap-2.5 bg-white/5 border border-white/20 hover:bg-white/10 text-white font-bold px-7 py-4 rounded-xl uppercase tracking-wider text-sm transition-all">
              Empezar el curso <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* BIENVENIDA */}
      <section id="curso" className="px-4 py-12 sm:py-16">
        <div className="max-w-4xl mx-auto bg-gradient-to-br from-slate-900/80 to-slate-950/80 border border-amber-400/30 rounded-3xl p-8 sm:p-12">
          <p className="text-[11px] text-amber-400 font-mono tracking-[3px] uppercase mb-3">Bienvenido al sistema</p>
          <h2 className="text-3xl sm:text-4xl font-black mb-5 leading-tight">Esto no es un folleto. Es un curso.</h2>
          <div className="space-y-4 text-slate-300 text-base sm:text-lg leading-relaxed">
            <p>Está diseñado para que entiendas — de verdad — por qué Travel Empire representa la ventaja competitiva más grande que un agente de viajes independiente puede tener hoy. Cada módulo construye sobre el anterior.</p>
            <p className="text-white font-bold">Lo que vas a aprender:</p>
            <ul className="space-y-2.5">
              {[
                "El verdadero costo de un equipo de marketing y por qué te quita tiempo y dinero.",
                "Cómo las grandes empresas dominan sus mercados con departamentos comerciales masivos.",
                "La ciencia detrás de la prospección: la matemática de los leads y las ventas.",
                "Las metas mínimas de conversión según tu etapa como agente.",
                "Por qué invertir poco es el mayor riesgo — y cómo Travel Empire lo elimina.",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-amber-300 flex-shrink-0 mt-0.5" />
                  <span className="text-base">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* MÓDULO 1 */}
      <Module n={1} kicker="Módulo 1" title="El Costo Real de un Squad">
        <p>Imagina que decides hacerlo todo por tu cuenta y armas tu propio equipo de marketing — lo que en la industria llamamos un “squad”. ¿Qué necesitarías contratar?</p>
        <div className="grid sm:grid-cols-2 gap-3 my-4">
          {ROLES.map((r) => (
            <div key={r} className="bg-slate-900/40 border border-white/10 rounded-xl p-4 flex items-start gap-3">
              <Users className="h-5 w-5 text-amber-300 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-slate-300">{r}</span>
            </div>
          ))}
        </div>
        <p className="text-white font-bold pt-2">Los números reales del mercado en 2025–2026:</p>
        <div className="overflow-x-auto rounded-2xl border border-white/10 my-3">
          <table className="w-full text-sm">
            <tbody>
              {COSTOS.map((row, i) => (
                <tr key={row.c} className={i % 2 ? "bg-white/[0.02]" : ""}>
                  <td className="py-3 px-4 text-slate-300">{row.c}</td>
                  <td className="py-3 px-4 text-amber-300 font-black whitespace-nowrap text-right">{row.v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-amber-400/10 border border-amber-400/30 rounded-xl p-5 flex items-start gap-3">
          <Clock className="h-6 w-6 text-amber-300 flex-shrink-0" />
          <p className="text-amber-100 text-base"><strong>Dato revelador:</strong> armar un equipo de cuatro personas toma de 6 a 8 meses solo para tenerlo funcionando. Reemplazar a un empleado de marketing cuesta el equivalente a 6–9 meses de su salario.</p>
        </div>
        <Quote>El error más común no es contratar caro. Es contratar tres generalistas mediocres en lugar de un sistema que ya funciona.</Quote>
      </Module>

      {/* MÓDULO 2 */}
      <Module n={2} kicker="Módulo 2" title="El Secreto de los Grandes">
        <p>La prospección sistematizada — generar clientes de forma constante y predecible mediante campañas pagadas — es un privilegio que históricamente solo las grandes empresas han podido permitirse. ¿Por qué? Porque tienen departamentos comerciales con cientos o miles de agentes, y eso diluye el costo del equipo de marketing.</p>
        <p>Las grandes corporaciones — desde aseguradoras hasta clubes vacacionales — comparten un mismo modelo: <strong className="text-amber-200">un departamento central de marketing alimenta de leads a un ejército de agentes.</strong> El agente nunca ve la maquinaria; solo recibe oportunidades y cierra.</p>
        <Quote>Si este sistema es lo que hace ricas a las grandes empresas… ¿por qué tú, como agente independiente, deberías jugar con reglas distintas? No deberías.</Quote>
        <div className="bg-slate-900/40 border border-white/10 rounded-xl p-5 flex items-start gap-3">
          <BarChart3 className="h-6 w-6 text-amber-300 flex-shrink-0" />
          <p className="text-slate-300">El 61% de los profesionales de marketing afirma que generar leads es su mayor desafío (HubSpot). Con Travel Empire, tú tendrás resuelto el problema más difícil del negocio desde el primer día.</p>
        </div>
      </Module>

      {/* MÓDULO 3 */}
      <Module n={3} kicker="Módulo 3" title="La Ciencia de la Prospección">
        <p>El éxito comercial no es magia ni suerte. Es matemática. Todo cliente que paga pasó primero por una serie de etapas: el <strong className="text-amber-200">embudo de ventas</strong>. No todos los leads compran — el secreto está en entender las proporciones.</p>
        <div className="grid sm:grid-cols-3 gap-4 my-4">
          {[
            { icon: TrendingUp, v: "5%", l: "Conversión promedio lead → cliente (Salesforce)" },
            { icon: Brain, v: "7.1%", l: "Con datos en tiempo real y personalización con IA" },
            { icon: Clock, v: "21x", l: "Más probable de calificar si respondes en 5 min" },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.l} className="bg-slate-900/40 border border-white/10 rounded-2xl p-5 text-center">
                <Icon className="h-7 w-7 text-amber-300 mx-auto mb-3" />
                <p className="text-3xl font-black text-amber-300 mb-1">{s.v}</p>
                <p className="text-xs text-slate-400 leading-snug">{s.l}</p>
              </div>
            );
          })}
        </div>
        <p>Las empresas que dan seguimiento en la primera hora reportan una conversión del <strong className="text-amber-200">53%</strong>, comparada con apenas 17% después de 24 horas. La velocidad lo es todo — pero sin volumen suficiente, ni el mejor cerrador del mundo puede construir un imperio.</p>
      </Module>

      {/* MÓDULO 4 */}
      <Module n={4} kicker="Módulo 4" title="Más Leads = Más Ceros">
        <p>Si tu tasa de cierre es constante — digamos 5% — entonces la única variable que controla tus ingresos es el número de leads que entran a tu embudo.</p>
        <Quote>La diferencia entre 20 leads y 600 leads al mes no es lineal — es transformacional. Es la diferencia entre sobrevivir y construir un patrimonio.</Quote>
        <p className="flex items-start gap-3"><Calculator className="h-6 w-6 text-amber-300 flex-shrink-0 mt-0.5" /> Cada lead adicional es una oportunidad de venta adicional. Cada oportunidad adicional es un cero más en tu próxima transferencia.</p>
      </Module>

      {/* MÓDULO 5 */}
      <Module n={5} kicker="Módulo 5" title="Tus Metas Según tu Etapa">
        <p>Un vendedor toma en promedio de 3 a 6 meses en alcanzar su productividad plena (la curva de “ramp-up”). Entender en qué etapa estás te permite fijar metas realistas.</p>
        <div className="grid lg:grid-cols-3 gap-4 my-4">
          {ETAPAS.map((e) => (
            <div key={e.n} className={`bg-slate-900/40 border ${e.box} rounded-2xl p-5 relative overflow-hidden`}>
              <div className={`absolute top-2 right-3 text-6xl font-black opacity-10 ${e.num}`}>{e.n}</div>
              <p className={`text-[10px] font-mono uppercase tracking-[2px] ${e.kick} mb-1`}>Etapa {e.n} · {e.t}</p>
              <h3 className="text-lg font-black text-white mb-3 leading-tight">{e.name}</h3>
              <ul className="space-y-2 text-sm text-slate-300">
                <li><span className="text-slate-500">Objetivo:</span> {e.obj}</li>
                <li><span className="text-slate-500">Leads:</span> <strong className="text-white">{e.leads}</strong></li>
                <li><span className="text-slate-500">Conversión:</span> <strong className="text-white">{e.conv}</strong></li>
                <li className={`${e.meta} font-bold pt-1`}>Meta: {e.metaTxt}</li>
              </ul>
            </div>
          ))}
        </div>
        <div className="bg-amber-400/10 border border-amber-400/30 rounded-xl p-5 flex items-start gap-3">
          <Target className="h-6 w-6 text-amber-300 flex-shrink-0" />
          <p className="text-amber-100"><strong>Lección clave:</strong> tu conversión sube con la experiencia, pero tus ventas se disparan con el volumen de leads. Combinar ambos es la fórmula del crecimiento exponencial.</p>
        </div>
      </Module>

      {/* MÓDULO 6 */}
      <Module n={6} kicker="Módulo 6" title="Invertir Poco es el Mayor Riesgo">
        <p>La mayoría cree que invertir poco es la opción “segura”. La realidad es exactamente lo opuesto. Con pocos leads, la matemática del embudo trabaja en tu contra: con solo 10 leads y 5% de cierre, habrá meses de cero ventas — no porque hagas algo mal, sino porque el volumen es demasiado bajo.</p>
        <Quote>Invertir poco no reduce tu riesgo. Lo multiplica. Porque pagas el costo de estar en el juego, pero sin el volumen suficiente para ganar.</Quote>
        <p className="text-white font-bold pt-2">La comparación que lo dice todo:</p>
        <div className="overflow-x-auto rounded-2xl border border-white/10 my-3">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-white/[0.03] text-left">
                <th className="py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-slate-400"></th>
                <th className="py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-rose-300">Hacerlo Solo</th>
                <th className="py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-emerald-300">Con Travel Empire</th>
              </tr>
            </thead>
            <tbody>
              {COMPARA.map((row, i) => (
                <tr key={row.c} className={i % 2 ? "bg-white/[0.02]" : ""}>
                  <td className="py-3 px-4 text-slate-300">{row.c}</td>
                  <td className="py-3 px-4 text-rose-200"><XCircle className="inline h-4 w-4 mr-1.5 mb-0.5" />{row.solo}</td>
                  <td className="py-3 px-4 text-emerald-200"><CheckCircle2 className="inline h-4 w-4 mr-1.5 mb-0.5" />{row.te}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-emerald-400/10 border border-emerald-400/30 rounded-xl p-5 flex items-start gap-3">
          <Rocket className="h-6 w-6 text-emerald-300 flex-shrink-0" />
          <p className="text-emerald-100">Cuando quitas el costo del equipo y el costo del tiempo, escalar deja de ser caro, lento y complicado. Se vuelve <strong>económico, rápido y simple.</strong></p>
        </div>
      </Module>

      {/* MÓDULO 7 */}
      <Module n={7} kicker="Módulo 7" title="Tu Inversión: Bajo Control">
        <p>Travel Empire te da control total: tú decides cuánto invertir por día, y nosotros escalamos las campañas en bloques quincenales.</p>
        <div className="overflow-x-auto rounded-2xl border border-white/10 my-3">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-white/[0.03] text-left">
                <th className="py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-amber-400">Inversión Diaria</th>
                <th className="py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-amber-400">Pago Quincenal</th>
                <th className="py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-amber-400">Perfil del Agente</th>
              </tr>
            </thead>
            <tbody>
              {INVERSION.map((row, i) => (
                <tr key={row.d} className={i % 2 ? "bg-white/[0.02]" : ""}>
                  <td className="py-3 px-4 text-white font-black whitespace-nowrap">{row.d}</td>
                  <td className="py-3 px-4 text-amber-300 font-bold">{row.q}</td>
                  <td className="py-3 px-4 text-slate-300">{row.p}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-slate-900/40 border border-white/10 rounded-xl p-5">
          <p className="text-white font-bold mb-2 flex items-center gap-2"><DollarSign className="h-5 w-5 text-amber-300" /> Reglas claras de la inversión:</p>
          <ul className="space-y-1.5 text-sm text-slate-300">
            <li>· Inversión mínima: <strong className="text-white">$10 dólares diarios.</strong></li>
            <li>· Se incrementa en bloques de $10 adicionales por día.</li>
            <li>· Período mínimo: 15 días calendario por campaña.</li>
            <li>· Pago 100% adelantado — las campañas se activan en bloques quincenales.</li>
            <li>· Todo se gestiona por día, para máximo control y transparencia.</li>
          </ul>
        </div>
      </Module>

      {/* CONCLUSIÓN + CTA */}
      <section className="px-4 py-14 sm:py-20">
        <div className="max-w-4xl mx-auto bg-gradient-to-br from-amber-500/15 via-slate-900/60 to-slate-950/80 border border-amber-400/40 rounded-3xl p-8 sm:p-12 text-center relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-52 h-52 rounded-full bg-amber-400/15 blur-3xl pointer-events-none" />
          <div className="relative">
            <Zap className="h-10 w-10 text-amber-300 mx-auto mb-4" />
            <h2 className="text-3xl sm:text-4xl font-black mb-4 leading-tight">Ya tienes el sistema.<br /><span className="text-amber-300">Lo único que falta es el combustible.</span></h2>
            <p className="text-slate-300 text-base sm:text-lg max-w-2xl mx-auto mb-3">
              Ya tienes las 5 líneas de negocio. Ya tienes la plataforma. Travel Empire es el combustible que mueve la máquina: la maquinaria de los grandes, a costo cero de equipo.
            </p>
            <p className="text-amber-200 font-bold mb-8">— Carlos Fernández · Fundador, ClubViajemos / Travel Empire</p>
            <a href={ACUITY} target="_blank" rel="noopener noreferrer" data-testid="tol-cta-final"
              className="inline-flex items-center justify-center gap-2.5 bg-gradient-to-r from-amber-400 to-amber-300 hover:from-amber-300 hover:to-amber-400 text-slate-900 font-black px-8 py-4 rounded-xl uppercase tracking-wider text-sm hover:scale-[1.03] transition-transform shadow-[0_15px_50px_rgba(212,175,55,0.4)]">
              <Phone className="h-5 w-5" /> Agenda tu cita de activación
            </a>
            <p className="text-xs text-slate-500 mt-5">Weston, Florida · www.clubviajemos.com</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TonsOfLeadsPage;
